import React, { useCallback, useMemo, useRef, useState } from 'react';

const STATUS_DEFINITIONS = [
  { key: 'Completed', label: 'Completed', colorClass: 'bootcamp-stack-completed' },
  { key: 'Missing Capstone', label: 'Missing Capstone', colorClass: 'bootcamp-stack-missing' },
  {
    key: 'Pending Capstone Review',
    label: 'Pending Capstone Review',
    colorClass: 'bootcamp-stack-review',
  },
  { key: 'Registered', label: 'Registered', colorClass: 'bootcamp-stack-registered' },
  { key: 'Failed', label: 'Failed', colorClass: 'bootcamp-stack-failed' },
  { key: 'No Show', label: 'No Show', colorClass: 'bootcamp-stack-noshow' },
];

const getRowTotal = (item) =>
  STATUS_DEFINITIONS.reduce((sum, status) => sum + (item[status.key] || 0), 0);

const CertificationOutcomesByCohortChart = ({ data, getShortCohortLabel }) => {
  const chartRef = useRef(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [hoveredLegend, setHoveredLegend] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  const activeStatuses = useMemo(() => {
    return STATUS_DEFINITIONS.filter((status) => {
      const total = data.reduce((sum, item) => sum + (item[status.key] || 0), 0);
      return total > 0;
    });
  }, [data]);

  const clampTooltipPosition = useCallback((clientX, clientY) => {
    const chartRect = chartRef.current?.getBoundingClientRect();
    const tooltipWidth = 220;
    const tooltipHeight = 88;
    const offset = 14;

    let x = clientX + offset;
    let y = clientY - tooltipHeight - offset;

    if (chartRect) {
      const maxX = chartRect.right - tooltipWidth - 8;
      const minX = chartRect.left + 8;
      const maxY = chartRect.bottom - tooltipHeight - 8;
      const minY = chartRect.top + 8;

      x = Math.min(Math.max(x, minX), maxX);
      y = Math.min(Math.max(y, minY), maxY);
    }

    return { x, y };
  }, []);

  const handleSegmentEnter = useCallback(
    (event, payload) => {
      setHoveredSegment(payload);
      setHoveredRow(payload.cohort);
      const { x, y } = clampTooltipPosition(event.clientX, event.clientY);
      setTooltip({ ...payload, x, y });
    },
    [clampTooltipPosition]
  );

  const handleSegmentMove = useCallback(
    (event, payload) => {
      const { x, y } = clampTooltipPosition(event.clientX, event.clientY);
      setTooltip((prev) => (prev ? { ...prev, ...payload, x, y } : null));
    },
    [clampTooltipPosition]
  );

  const handleSegmentLeave = useCallback(() => {
    setHoveredSegment(null);
    setTooltip(null);
  }, []);

  const handleRowEnter = useCallback((cohort) => {
    setHoveredRow(cohort);
  }, []);

  const handleRowLeave = useCallback(() => {
    setHoveredRow(null);
    if (!hoveredLegend) {
      setHoveredSegment(null);
      setTooltip(null);
    }
  }, [hoveredLegend]);

  const handleLegendEnter = useCallback((statusKey) => {
    setHoveredLegend(statusKey);
    setTooltip(null);
    setHoveredSegment(null);
  }, []);

  const handleLegendLeave = useCallback(() => {
    setHoveredLegend(null);
  }, []);

  const getSegmentState = (cohort, statusKey) => {
    if (hoveredLegend) {
      return hoveredLegend === statusKey ? 'highlighted' : 'dimmed';
    }
    if (hoveredSegment) {
      if (hoveredSegment.cohort === cohort && hoveredSegment.statusKey === statusKey) {
        return 'highlighted';
      }
      if (hoveredSegment.cohort === cohort) {
        return 'dimmed';
      }
    }
    return 'default';
  };

  const pct = (value, total) => (total ? Math.max(value > 0 ? 1.5 : 0, (value / total) * 100) : 0);

  return (
    <div className="bootcamp-cohort-outcomes-chart" ref={chartRef}>
      <div className="bootcamp-stacked-list">
        {data.map((item) => {
          const total = getRowTotal(item);
          const shortLabel = getShortCohortLabel(item.cohort);
          const isRowHovered = hoveredRow === item.cohort;

          return (
            <div
              className={`bootcamp-cohort-row bootcamp-stacked-row ${isRowHovered ? 'is-hovered' : ''}`}
              key={item.cohort}
              onMouseEnter={() => handleRowEnter(item.cohort)}
              onMouseLeave={handleRowLeave}
            >
              <div className="bootcamp-stacked-row-head">
                <span title={item.cohort}>{shortLabel}</span>
                <strong className={`bootcamp-cohort-row-total ${isRowHovered ? 'is-emphasized' : ''}`}>
                  {total}
                </strong>
              </div>
              <div className={`bootcamp-stacked-track ${isRowHovered ? 'is-hovered' : ''}`}>
                {activeStatuses.map((status) => {
                  const value = item[status.key] || 0;
                  if (value <= 0) {
                    return null;
                  }

                  const segmentState = getSegmentState(item.cohort, status.key);
                  const percentage = total ? Math.round((value / total) * 100) : 0;
                  const ariaLabel = `${shortLabel}, ${status.label}, ${value} participants`;

                  return (
                    <span
                      key={status.key}
                      className={`bootcamp-stacked-segment bootcamp-stack-segment ${status.colorClass} ${
                        segmentState === 'highlighted' ? 'is-highlighted' : ''
                      } ${segmentState === 'dimmed' ? 'is-dimmed' : ''}`}
                      style={{ width: `${pct(value, total)}%` }}
                      role="img"
                      aria-label={ariaLabel}
                      onMouseEnter={(event) =>
                        handleSegmentEnter(event, {
                          cohort: item.cohort,
                          cohortLabel: shortLabel,
                          statusKey: status.key,
                          statusLabel: status.label,
                          count: value,
                          total,
                          percentage,
                        })
                      }
                      onMouseMove={(event) =>
                        handleSegmentMove(event, {
                          cohort: item.cohort,
                          cohortLabel: shortLabel,
                          statusKey: status.key,
                          statusLabel: status.label,
                          count: value,
                          total,
                          percentage,
                        })
                      }
                      onMouseLeave={handleSegmentLeave}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="bootcamp-stacked-legend bootcamp-cohort-outcomes-legend">
        {activeStatuses.map((status) => {
          const isActive = hoveredLegend === status.key;
          const isDimmed = hoveredLegend && hoveredLegend !== status.key;

          return (
            <span
              key={status.key}
              className={`bootcamp-legend-item ${isActive ? 'bootcamp-legend-item-active is-active' : ''} ${
                isDimmed ? 'is-dimmed' : ''
              }`}
              onMouseEnter={() => handleLegendEnter(status.key)}
              onMouseLeave={handleLegendLeave}
            >
              <i className={`bootcamp-dot ${status.colorClass}`} />
              {status.label}
            </span>
          );
        })}
      </div>

      {tooltip && (
        <div
          className="bootcamp-chart-tooltip"
          style={{ left: tooltip.x, top: tooltip.y }}
          role="tooltip"
        >
          <p className="bootcamp-chart-tooltip-title">{tooltip.cohortLabel}</p>
          <p className="bootcamp-chart-tooltip-line">
            <strong>{tooltip.statusLabel}:</strong> {tooltip.count}
          </p>
          <p className="bootcamp-chart-tooltip-meta">
            {tooltip.percentage}% of {tooltip.total} participants
          </p>
        </div>
      )}
    </div>
  );
};

export default CertificationOutcomesByCohortChart;
